import { makeAutoObservable, runInAction } from 'mobx'
import { orderListMobx, restaurantConfig, storeMobx } from '..'
import { get, sortBy, cloneDeep, keyBy, groupBy } from '@dt/fe-utils/dist/lodash'
import { parseString } from '@dt/fe-utils'
import { deskImg } from './_desk'
import StoreAPI from 'api/store.api'
import DeskAPI from 'api/desk.api'

class Desk {
  constructor () {
    makeAutoObservable(this)
  }

  /** 保存的原始数据 */
  areas: IDeskArea[] = []

  /** 当前选中的分类id ，togo则areaId为0 */
  areaId: number = 0

  /** 桌台员工过滤 */
  filterUser: string = 'all'

  /** 当前门店被占用的桌台 */
  storeOccupyTableList: IOccupyTableList[] = []

  /** tableCode 桌台信息 map  */
  tableCodeToDeskMap: { [tableCode: string]: any } = {}

  /** tableCode 桌台区域id map  */
  tableCodeToAreaIdMap: { [tableCode: string]: any } = {}

  /** tableCode 桌台别名 map  */
  tableCodeToTableAliasMap: { [tableCode: string]: string } = {}

  /** tableCode 桌台别名(无下划线) map  */
  tableCodeToTableAliasNoLineMap: { [tableCode: string]: string } = {}

  /** 当前区域桌台 */
  get tables (): IDesk[] {
    if (this.areaId > 0) {
      const theArea = this.areas.find(d => d.id === this.areaId)
      return theArea ? theArea.desks || [] : []
    } else {
      return []
    }
  }

  /** 当前区域桌台 view  */
  get viewDesks () {
    // 以长宽小的那个基准缩放
    const multipleW = (window.innerWidth - 56) / 721
    const multipleH = (window.innerHeight - 57) / 481
    const multiple = multipleW > multipleH ? multipleH : multipleW
    // const multiple = (window.innerHeight - 57) / 481
    // make sure tableCode is exist
    return this.tables.filter(_item => _item.tableCode || _item.type === 'door' || _item.type === 'wall').map(_item => {
      const item: IViewDesk = { ..._item }
      const width = item.width * multiple
      const height = item.height * multiple
      let fontSize = '20px'
      if (item.size === 2) {
        fontSize = '16px'
      }
      if (item.size === 4) {
        fontSize = '24px'
      }
      // 单桌特殊
      let singleAngle = 0
      item.positionStyle = {
        left: (item.left * multiple - width / 2) + 'px',
        top: (item.top * multiple - height / 2) + 'px'
      }
      item.transfromStyle = {
        transform: `rotate(${singleAngle || item.angle}deg) scaleX(${item.scaleX}) scaleY(${item.scaleY})`
      }
      item.textStyle = {
        fontSize: fontSize
      }
      item.tableStyle = {
        width: width + 'px',
        height: height + 'px',
        transform: `rotate(${singleAngle || item.angle}deg) scaleX(${item.scaleX}) scaleY(${item.scaleY})`
      }
      if (item.type === 'door') {
        const scaleX = item.scaleX ? parseFloat(item.scaleX) : 1
        const scaleY = item.scaleY ? parseFloat(item.scaleY) : 1
        item.positionStyle = {
          left: (item.left * multiple - width * scaleX / 2) + 'px',
          top: (item.top * multiple - height * scaleY / 2) + 'px'
        }
        item.tableStyle.width = width * scaleX + 'px'
        item.tableStyle.height = height * scaleY + 'px'
        //  translate(${width * multiple * scaleX / 2}px, ${height * multiple * scaleY / 2}px)
        item.tableStyle.transform = `rotate(${singleAngle || item.angle}deg) translate(${width * scaleX / 2}px, ${height * scaleY / 2}px)`
        item.tableStyle.backgroundImage = `url(${(deskImg as any)[item.type + '_' + item.size]})`
      } else if (item.type === 'wall') {
        item.name = ''
      } else {
        item.tableStyle.backgroundImage = `url(${(deskImg as any)[item.type + '_' + item.seat]})`
      }
      return item
    })
  }

  /** tableCode订单状态 map */
  get tableCodeToTableStatusMap (): Record<string, IOccupyTableList | TTableOrder> {
    const list = cloneDeep(this.storeOccupyTableList)
    return list.reduce((t, c) => ({ ...t, [c.tableCode]: Object.assign(c, get(this.tableCodeToOrderInfoMap, c.tableCode) || {}) }), {})
  }

  /** tableCode订单信息 map */
  get tableCodeToOrderInfoMap () {
    const tableCodeToOrderInfoMap: ITableCodeToOrderInfoMap = {}
    const storeOccupyTableMap = keyBy(this.storeOccupyTableList, 'tableCode')
    for (const tableCode in orderListMobx.tableCodeToOrders) {
      const tableOrder = storeOccupyTableMap?.[tableCode]
      const list = orderListMobx.tableCodeToOrders[tableCode]?.filter(order => tableOrder?.ordersId === order.ordersId)
      const order = list?.length > 1 ? list?.find(order => order.status === 'Pending' || order.status === 'Paying' || order.status === 'NotAccepted') : list?.[0]
      if (order) {
        const prePayPrintTime = order.extList && order.extList.find(item => item.attrName === 'prePayPrintTime')
        tableCodeToOrderInfoMap[tableCode] = {
          hasPrintGuestTicket: !!prePayPrintTime,
          addTime: storeMobx.storeExtInfoMap.reservationPrintStatus === 'enable' ? prePayPrintTime?.attrValue || order.acceptTime || order.addTime : order.acceptTime || order.addTime,
          colorAddTime: order.acceptTime || order.addTime,
          isSplit: !!order.parentId,
          status: 'open',
          nono: order.nono,
          isWait: order.status === 'NotAccepted',
          ordersId: order.ordersId, // 当前只考虑单桌
          person: order.person,
          addUserId: order.addUserId,
          addUserName: order.addUserName,
          originalFee: order.originalFee,
          paidFee: order.paidFee || 0,
          tax: order.tax || 0,
        }
      }
    }
    return tableCodeToOrderInfoMap
  }

  /** 桌台 callPos 订单 */
  get deskOrders () {
    return sortBy((orderListMobx.tableCodeToOrders[''] || [])
      .filter(order => order.payMode && order.channel === 'callPos')
      .map(order => {
        const prePayPrintTime = order.extList && order.extList.find(item => item.attrName === 'prePayPrintTime')
        return {
          hasPrintGuestTicket: !!prePayPrintTime,
          id: order.id,
          appType: order.appType,
          addTime: storeMobx.storeExtInfoMap.reservationPrintStatus === 'enable' ? prePayPrintTime?.attrValue || order.acceptTime || order.addTime : order.acceptTime || order.addTime,
          colorAddTime: order.acceptTime || order.addTime,
          isSplit: !!order.parentId,
          nono: order.nono,
          contactName: order.contactName || '',
          contactMobile: order.contactMobile || '',
          contactInfo: parseString(order.contactInfo) || {},
          status: 'open',
          ordersId: order.ordersId, // 当前只考虑单桌
          sourceType: order.sourceType,
          channel: order.channel,
          eatType: order.eatType
        }
      }), 'id')
  }

  /** 桌台 togo 订单 */
  get togoOrders () {
    return sortBy((orderListMobx.tableCodeToOrders[''] || [])
      .filter(order => order.payMode && order.eatType === 'togo')
      .map(order => {
        return {
          id: order.id,
          appType: order.appType,
          addTime: order.addTime,
          isSplit: !!order.parentId,
          nono: order.nono,
          contactName: order.contactName || '',
          contactMobile: order.contactMobile || '',
          status: 'open',
          ordersId: order.ordersId // 当前只考虑单桌
        }
      }), 'id')
  }

  /** 桌台 thirdDelivery 订单 */
  get thirdDeliveryOrders () {
    return sortBy((orderListMobx.tableCodeToOrders[''] || [])
      .filter(order => order.payMode && order.channel === '3rdDelivery').map(order => {
        return {
          id: order.id,
          appType: order.appType,
          addTime: order.addTime,
          nono: order.nono,
          contactName: order.contactName || '',
          contactMobile: order.contactMobile || '',
          status: 'open',
          ordersId: order.ordersId, // 当前只考虑单桌
          ordersOutNo: order.ordersOutNo,
          sourceType: order.sourceType,
          paidFee: order.paidFee,
          contactAddress: order.contactInfo
        }
      }), 'id')
  }

  /** 桌台 delivery 订单 */
  get deliveryOrders () {
    return []
  }

  /** 桌台区域订单数量 map */
  get areaToOrderCntMap () {
    const areaIdToOrderCntMap: { [areaId: number]: number } = {}
    for (const tableCode in this.tableCodeToOrderInfoMap) {
      const areaId = this.tableCodeToAreaIdMap[tableCode]
      areaIdToOrderCntMap[areaId] = areaIdToOrderCntMap[areaId] ? ++areaIdToOrderCntMap[areaId] : 1
    }
    return areaIdToOrderCntMap
  }

  /** 桌台状态 addUserId map  */
  get addUserDeskOrder () {
    return groupBy(this.storeOccupyTableList, 'addUserId')
  }

  /** 初始化桌台 */
  async init (storeId?: number) {
    const data = await StoreAPI.getTable(storeId)
    runInAction(() => {
      if (restaurantConfig.app === 'pos') {
        this.areas = data || []
        if (!this.areaId || this.areaId === 0) {
          this.areaId = this.areas.length ? this.areas[0].id : 0
        }
      } else {
        this.areas = [{ id: 0, name: '' }, ...data]
        if (bo !== 'cn') {
          this.areas.unshift({ id: -1, name: '' })
        }
        if (!this.areaId) {
          this.areaId = this.areas.length ? this.areas[0].id : 0
        }
      }

      // 设置相关 map
      this.setTableCodeMap()
    })
    return data
  }

  /** 选择区域 */
  selectArea (area: IDeskArea | number) {
    this.areaId = typeof area === 'number' ? area : (area?.id || 0)
  }

  /** 更换过滤接单服务员 */
  changeFilterUser (key: string) {
    this.filterUser = key
  }

  // 设置 tableCode 相关 map
  setTableCodeMap () {
    const deskMap: any = {}
    const areaIdMap: any = {}
    const tableAliasMap: any = {}
    const tableAliasNoLineMap: any = {}
    this.areas.forEach(area => {
      (area.desks || []).forEach(desk => {
        deskMap[desk.tableCode] = desk
        areaIdMap[desk.tableCode] = area.id
        tableAliasMap[desk.tableCode] = `${area.name}-${desk.name}`
        tableAliasNoLineMap[desk.tableCode] = `${area.name}${desk.name}`
      })
    })
    this.tableCodeToDeskMap = deskMap
    this.tableCodeToAreaIdMap = areaIdMap
    this.tableCodeToTableAliasMap = tableAliasMap
    this.tableCodeToTableAliasNoLineMap = tableAliasNoLineMap
  }

  /** 获取门店桌台状态 */
  async getTableStatusList () {
    const res = await DeskAPI.getTableStatusList().then((res: any) => (res?.result || []))
    runInAction(() => {
      this.storeOccupyTableList = res
    })
  }
}

export default new Desk()
